import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

interface SeoSetOptions {
  title: string;
  description?: string;
  path?: string;
  image?: string;
  robots?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly siteName = '밀리언쇼';
  private readonly siteUrl = 'https://www.millionshow.kr';
  readonly defaultTitle = this.siteName;
  readonly defaultDescription =
    '밀리언쇼의 박람회 일정, 행사 안내, 사전등록, 부스 신청, 갤러리, 고객지원 정보를 확인해보세요.';
  private readonly defaultImage =
    'https://kr.object.ncloudstorage.com/millionshow-assets/bccd757a-7fa8-441e-aafe-ed16b70885a4.png';

  private readonly titleService = inject(Title);
  private readonly metaService = inject(Meta);
  private readonly document = inject(DOCUMENT);

  set({
    title,
    description = this.defaultDescription,
    path,
    image = this.defaultImage,
    robots = 'index, follow',
  }: SeoSetOptions): void {
    const pageTitle =
      title === this.siteName ? title : `${title} | ${this.siteName}`;
    const canonicalUrl = new URL(
      path || this.document.location.pathname,
      this.siteUrl
    ).toString();

    this.applyMeta({
      title: pageTitle,
      description,
      robots,
      canonicalUrl,
      image,
    });
  }

  setDefault(): void {
    this.applyMeta({
      title: this.defaultTitle,
      description: this.defaultDescription,
      robots: 'index, follow',
      canonicalUrl: this.siteUrl,
      image: this.defaultImage,
    });
  }

  private updateCanonicalLink(url: string): void {
    let link = this.document.head.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  private applyMeta({
    title,
    description,
    robots,
    canonicalUrl,
    image,
  }: {
    title: string;
    description: string;
    robots: string;
    canonicalUrl: string;
    image: string;
  }): void {
    this.titleService.setTitle(title);

    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ name: 'robots', content: robots });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({
      property: 'og:description',
      content: description,
    });
    this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });
    this.metaService.updateTag({ property: 'og:image', content: image });

    this.updateCanonicalLink(canonicalUrl);
  }
}
